import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, SEO } from '../../components/common';
import {
  FiFilter, FiBell, FiTrendingUp, FiDollarSign, FiAlertCircle,
  FiSettings, FiFileText, FiMail, FiMessageCircle, FiSend,
  FiCalendar, FiActivity,
} from 'react-icons/fi';

interface NotificationItem {
  id: number;
  category: 'booking' | 'market' | 'transaction' | 'alert' | 'portfolio';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

interface NotificationPref {
  id: string;
  icon: any;
  title: string;
  description: string;
}

// Mock notification data relevant to the investment platform
const mockNotifications: NotificationItem[] = [
  {
    id: 1,
    category: 'booking',
    title: 'Booking Confirmed',
    message: 'Your session with Dr. Priya Sharma has been confirmed for tomorrow at 10:00 AM',
    time: '2 min ago',
    read: false,
  },
  {
    id: 2,
    category: 'market',
    title: 'Price Alert',
    message: 'RELIANCE has crossed ₹3,000 — up 3.1% today',
    time: '15 min ago',
    read: false,
  },
  {
    id: 3,
    category: 'transaction',
    title: 'Payment Successful',
    message: 'Your payment of ₹600 for expert consultation has been processed',
    time: '1 hour ago',
    read: true,
  },
  {
    id: 4,
    category: 'portfolio',
    title: 'Portfolio Doctor Update',
    message: 'New AI verdict available for TCS — recommendation changed to ADD',
    time: '3 hours ago',
    read: false,
  },
  {
    id: 5,
    category: 'booking',
    title: 'Session Reminder',
    message: 'Your consultation with Rahul Verma starts in 30 minutes',
    time: '5 hours ago',
    read: true,
  },
  {
    id: 6,
    category: 'market',
    title: 'Weekly Report',
    message: 'Your portfolio gained 2.5% this week. View detailed report.',
    time: '1 day ago',
    read: true,
  },
  {
    id: 7,
    category: 'transaction',
    title: 'Wallet Credited',
    message: '₹5,000 has been added to your wallet',
    time: '2 days ago',
    read: true,
  },
  {
    id: 8,
    category: 'alert',
    title: 'Nifty 50 Update',
    message: 'Nifty 50 closes at all-time high. Check the latest market analysis.',
    time: '2 days ago',
    read: true,
  },
];

const notificationPrefs: NotificationPref[] = [
  { id: 'market_alerts', icon: FiTrendingUp, title: 'Market Alerts', description: 'Price alerts and market movements' },
  { id: 'portfolio', icon: FiActivity, title: 'Portfolio Updates', description: 'AI verdicts and portfolio changes' },
  { id: 'bookings', icon: FiCalendar, title: 'Booking Reminders', description: 'Expert session confirmations and reminders' },
  { id: 'transactions', icon: FiDollarSign, title: 'Transactions', description: 'Wallet credits, debits, and payments' },
  { id: 'research', icon: FiFileText, title: 'Research Reports', description: 'New research reports and insights' },
  { id: 'email', icon: FiMail, title: 'Email Notifications', description: 'Receive updates via email' },
  { id: 'whatsapp', icon: FiMessageCircle, title: 'WhatsApp', description: 'Receive alerts on WhatsApp' },
  { id: 'telegram', icon: FiSend, title: 'Telegram', description: 'Receive alerts on Telegram' },
];

const Notification: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  const [activeTab, setActiveTab] = useState<'notifications' | 'settings'>('notifications');
  const [showSortModal, setShowSortModal] = useState(false);
  const [notifications, setNotifications] = useState(mockNotifications);
  const [toggles, setToggles] = useState<Record<string, boolean>>({
    market_alerts: true,
    portfolio: true,
    bookings: true,
    transactions: true,
    research: false,
    email: true,
    whatsapp: false,
    telegram: false,
  });

  const getIcon = (category: string) => {
    switch (category) {
      case 'booking': return FiBell;
      case 'market': return FiTrendingUp;
      case 'transaction': return FiDollarSign;
      case 'portfolio': return FiActivity;
      case 'alert': return FiAlertCircle;
      default: return FiBell;
    }
  };

  const getIconColor = (category: string) => {
    switch (category) {
      case 'booking': return colors.primary;
      case 'market': return colors.green;
      case 'transaction': return '#FFD700';
      case 'portfolio': return '#8b5cf6';
      case 'alert': return colors.alertColor;
      default: return colors.primary;
    }
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
    );
    switch (notification.category) {
      case 'booking': navigate('/bookings'); break;
      case 'market': navigate('/market'); break;
      case 'transaction': navigate('/wallet'); break;
      case 'portfolio': navigate('/portfolio'); break;
      default: break;
    }
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleToggle = (id: string) => {
    setToggles((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div
      className="min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO title="Notifications" noIndex />

      {/* Header */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)}>
            <CText type="B16" color={colors.textColor}>&larr;</CText>
          </button>
          <CText type="B18" color={colors.textColor}>
            Notifications
          </CText>
          {activeTab === 'notifications' ? (
            <button onClick={() => setShowSortModal(!showSortModal)}>
              {React.createElement(FiFilter as any, { size: 20, color: colors.textColor })}
            </button>
          ) : (
            <div style={{ width: 20 }} />
          )}
        </div>

        {/* Tab Selector: Notifications | Settings */}
        <div
          className="flex rounded-xl p-1 mb-4"
          style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
        >
          {([
            { key: 'notifications' as const, label: 'Notifications', icon: FiBell },
            { key: 'settings' as const, label: 'Settings', icon: FiSettings },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg transition-colors"
              style={{
                backgroundColor: activeTab === tab.key ? colors.primary : 'transparent',
              }}
            >
              {React.createElement(tab.icon as any, {
                size: 14,
                color: activeTab === tab.key ? '#fff' : colors.grayScale500,
              })}
              <CText
                type="M12"
                color={activeTab === tab.key ? '#fff' : colors.grayScale500}
              >
                {tab.label}
              </CText>
              {tab.key === 'notifications' && unreadCount > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: activeTab === tab.key ? 'rgba(255,255,255,0.3)' : colors.alertColor,
                    color: '#fff',
                    fontSize: 10,
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ NOTIFICATIONS TAB ═══ */}
      {activeTab === 'notifications' && (
        <div className="px-4">
          {/* Unread indicator & Mark all read */}
          {unreadCount > 0 && (
            <div className="flex items-center justify-between mb-4">
              <CText type="r12" color={colors.grayScale500}>
                {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
              </CText>
              <button onClick={markAllAsRead}>
                <CText type="M12" color={colors.primary}>
                  Mark all as read
                </CText>
              </button>
            </div>
          )}

          {/* Notification list */}
          {notifications.length === 0 ? (
            <div className="text-center py-12">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: colors.primary + '15' }}
              >
                {React.createElement(FiBell as any, { size: 28, color: colors.primary })}
              </div>
              <CText type="M14" color={colors.textColor} style={{ marginBottom: 4 }}>
                No notifications yet
              </CText>
              <CText type="r12" color={colors.grayScale500}>
                You'll see market alerts, booking updates, and more here.
              </CText>
            </div>
          ) : (
            notifications.map((notification) => {
              const IconComponent = getIcon(notification.category);
              const iconColor = getIconColor(notification.category);

              return (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className="w-full flex items-start gap-3 py-4 border-b text-left"
                  style={{ borderColor: colors.borderColor }}
                >
                  <div
                    className="p-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: iconColor + '20' }}
                  >
                    {React.createElement(IconComponent as any, { size: 18, color: iconColor })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CText
                        type={notification.read ? 'r14' : 'M14'}
                        color={colors.textColor}
                      >
                        {notification.title}
                      </CText>
                      {!notification.read && (
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: colors.primary }}
                        />
                      )}
                    </div>
                    <CText type="r12" color={colors.grayScale500} style={{ marginTop: 2 }}>
                      {notification.message}
                    </CText>
                  </div>
                  <CText type="r10" color={colors.grayScale400} className="flex-shrink-0 pt-1">
                    {notification.time}
                  </CText>
                </button>
              );
            })
          )}
        </div>
      )}

      {/* ═══ SETTINGS TAB ═══ */}
      {activeTab === 'settings' && (
        <div className="px-4">
          <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 16 }}>
            Choose which notifications you'd like to receive.
          </CText>

          <div className="space-y-2.5">
            {notificationPrefs.map((item) => {
              const Icon = item.icon;
              const isEnabled = toggles[item.id];

              return (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 rounded-xl border"
                  style={{
                    backgroundColor: colors.inputBackground,
                    borderColor: colors.borderColor,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: colors.primary + '15' }}
                    >
                      {React.createElement(Icon as any, { size: 18, color: colors.primary })}
                    </div>
                    <div>
                      <CText type="M14" color={colors.textColor}>
                        {item.title}
                      </CText>
                      <CText type="r12" color={colors.grayScale500}>
                        {item.description}
                      </CText>
                    </div>
                  </div>

                  {/* Toggle Switch */}
                  <button
                    onClick={() => handleToggle(item.id)}
                    className="w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ml-3"
                    style={{
                      backgroundColor: isEnabled ? colors.primary : colors.grayScale500 + '40',
                    }}
                  >
                    <div
                      className="absolute top-1 w-4 h-4 rounded-full transition-all bg-white"
                      style={{ left: isEnabled ? '28px' : '4px' }}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sort Modal */}
      {showSortModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50"
          onClick={() => setShowSortModal(false)}
        >
          <div
            className="w-full max-w-md rounded-t-2xl p-4"
            style={{ backgroundColor: colors.backgroundColor }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <CText type="B16" color={colors.textColor} style={{ marginBottom: 16 }}>
              Sort Notifications
            </CText>

            {['Newest first', 'Oldest first', 'Unread first'].map((option) => (
              <button
                key={option}
                className="w-full py-3 text-left border-b"
                style={{ borderColor: colors.borderColor }}
                onClick={() => setShowSortModal(false)}
              >
                <CText type="r14" color={colors.textColor}>
                  {option}
                </CText>
              </button>
            ))}

            <button
              className="w-full py-4 mt-4 rounded-xl"
              style={{ backgroundColor: colors.primary }}
              onClick={() => setShowSortModal(false)}
            >
              <CText type="M14" color={colors.white}>
                Done
              </CText>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notification;
